import {
  Component,
  OnInit,
  inject,
  OnDestroy,
  Input,
  ChangeDetectorRef,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormGroupDirective,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatTabsModule } from "@angular/material/tabs";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Store } from "@ngrx/store";
import { Subject, takeUntil, debounceTime, take } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import {
  selectActivePacket,
  selectMultiPacketMode,
  selectTraditionalPacket,
  selectMvpPacket,
  selectQualityMeasures,
  selectQualityMeasuresLoading,
  selectRelatedOrganizations,
  selectEffectiveQuestionnaire,
  selectIsSaving,
} from "@store/selectors/mips-packet/mips-packet.selector";
import { selectUserAdminFilteredOrgs } from "@store/selectors/user-admin/user-admin.selector";
import { QualityMeasure, QualityCategory } from "@models/mips-stepper.model";
import { MipsCalculationService } from "@services/mips-calculation.service";
import {
  MipsApiService,
  QualityMeasurementDto,
} from "@services/mips-api.service";
import { selectDeciles } from "@store/selectors/dashboard/dashboard.selectors";
import { DashboardMeasureDecile } from "@models/dashboard-measure-deciles.model";
import measureDataJson from "../dashboard/measureData.json";
import { MipsReweightingService } from "@services/mips-reweighting.service";
import { MipsValidationService } from "@services/mips-validation.service";


@Component({
  selector: "app-mips-quality",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatCheckboxModule,
    MatTabsModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: "./mips-quality.component.html",
  styleUrls: ["./mips-quality.component.css"],
})
export class MipsQualityComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup;

  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly ctrlContainer = inject(FormGroupDirective);
  private readonly mipsCalc = inject(MipsCalculationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly mipsApi = inject(MipsApiService);
  private readonly destroy$ = new Subject<void>();
  private readonly measureSelectionChanged$ = new Subject<void>();
  private readonly reweightingService = inject(MipsReweightingService);
  private readonly validationService = inject(MipsValidationService);

  questionnaire = toSignal(this.store.select(selectEffectiveQuestionnaire));
  reweighting = this.reweightingService.getReweightingSignal();
  currentPacket = toSignal(this.store.select(selectActivePacket));
  isScoreCalculating = toSignal(this.store.select(selectIsSaving), { initialValue: false });
  traditionalPacket = toSignal(this.store.select(selectTraditionalPacket));
  mvpPacket = toSignal(this.store.select(selectMvpPacket));
  qualityMeasures = toSignal(this.store.select(selectQualityMeasures));
  measuresLoading = toSignal(this.store.select(selectQualityMeasuresLoading), { initialValue: false });
  multiPacketMode = toSignal(this.store.select(selectMultiPacketMode));
  relatedOrganizations = toSignal(this.store.select(selectRelatedOrganizations), { initialValue: [] });

  qualityForm!: FormGroup;
  allMeasures: QualityMeasure[] = [];
  traditionalMeasures: QualityMeasure[] = [];
  mvpMeasures: QualityMeasure[] = [];
  traditionalSelectedIds: Set<string> = new Set();
  mvpSelectedIds: Set<string> = new Set();
  expandedMeasureId: string | null = null;
  currentTab: "traditional" | "mvp" = "traditional";
  private readonly decilesSignal = toSignal(this.store.select(selectDeciles), { initialValue: [] as DashboardMeasureDecile[] });
  get measureDeciles(): DashboardMeasureDecile[] {
    return this.decilesSignal();
  }
  isSmallPractice = false;
  private measuresLoaded = false;
  private currentPacketId: number | null = null;
  private measureStrataMap: Map<string, any[]> = new Map();

  searchText: string = "";
  sortColumn: string = "";
  sortDirection: "asc" | "desc" = "asc";


  get practice(): string {
    const practiceId = this.currentPacket()?.practice || "";

    if (!practiceId) {
      return "";
    }

    let orgName = practiceId;

    this.store
      .select(selectUserAdminFilteredOrgs)
      .pipe(take(1))
      .subscribe((orgs) => {
        if (orgs) {
          const org = orgs.find((o) => o.id.toString() === practiceId);
          if (org) {
            orgName = org.name;
          }
        }
      });

    return orgName;
  }

  get rollupTooltip(): string {
    const orgs = [this.practice, ...this.relatedOrganizations().map(o => o.organizationName)];
    return orgs.filter(Boolean).join(", ");
  }

  get performanceDuration(): string {
    const packet = this.currentPacket();
    const start = packet?.tinDetails?.performanceStartDate;
    const end = packet?.tinDetails?.performanceEndDate;
    if (start && end) {
      return `${this.formatDate(start)} - ${this.formatDate(end)}`;
    }
    return "";
  }

  get tin(): string {
    return this.currentPacket()?.tinDetails?.tin || "N/A";
  }

  get reportingType(): string {
    const mode = this.multiPacketMode();
    if (mode?.enabled && mode?.mode) {
      return mode.mode;
    }
    return this.currentPacket()?.eligibility?.reportingType || "traditional";
  }

  get showTabs(): boolean {
    return this.reportingType === "both";
  }

  get activeMeasures(): QualityMeasure[] {
    if (!this.showTabs) {
      return this.reportingType === "mvp"
        ? this.mvpMeasures
        : this.traditionalMeasures;
    } else {
      return this.currentTab === "mvp"
        ? this.mvpMeasures
        : this.traditionalMeasures;
    }
  }

  get filteredAndSortedMeasures(): QualityMeasure[] {
    let measures = [...this.activeMeasures];

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      measures = measures.filter(
        (m) =>
          m.measureId.toLowerCase().includes(search) ||
          m.measureTitle.toLowerCase().includes(search),
      );
    }

    if (this.sortColumn) {
      measures.sort((a, b) => {
        let aVal: any;
        let bVal: any;

        switch (this.sortColumn) {
          case "id":
            aVal = a.measureId;
            bVal = b.measureId;
            break;
          case "title":
            aVal = a.measureTitle;
            bVal = b.measureTitle;
            break;
          case "performance":
            aVal = a.performance || 0;
            bVal = b.performance || 0;
            break;
          case "points":
            aVal = a.calculatedPoints || 0;
            bVal = b.calculatedPoints || 0;
            break;
          case "denominator":
            aVal = a.denominator || 0;
            bVal = b.denominator || 0;
            break;
          default:
            return 0;
        }

        if (typeof aVal === "string") {
          return this.sortDirection === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        } else {
          return this.sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        }
      });
    }

    return measures;
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortColumn = column;
      this.sortDirection = "asc";
    }
  }

  clearSearch(): void {
    this.searchText = "";
  }

  get selectedMeasures(): QualityMeasure[] {
    const selectedSet =
      (this.showTabs && this.currentTab === "mvp") ||
      (!this.showTabs && this.reportingType === "mvp")
        ? this.mvpSelectedIds
        : this.traditionalSelectedIds;

    return this.activeMeasures.filter((m) => selectedSet.has(m.measureId));
  }

  get traditionalSelectedCount(): number {
    return this.traditionalMeasures.filter((m) =>
      this.traditionalSelectedIds.has(m.measureId),
    ).length;
  }

  get mvpSelectedCount(): number {
    return this.mvpMeasures.filter((m) => this.mvpSelectedIds.has(m.measureId))
      .length;
  }

  get minimumRequired(): number {
    return 3;
  }

  get hasOutcomeHighPriority(): boolean {
    return this.selectedMeasures.some((m) => m.isOutcome || m.isHighPriority);
  }

  get hasSelectedEcqmMeasures(): boolean {
    return this.selectedMeasures.some((m) => m.measureType === "CMS eCQM");
  }

  get hasCehrtId(): boolean {
    return !!this.currentPacket()?.settings?.cehrtId;
  }

  get showCehrtWarning(): boolean {
    return this.hasSelectedEcqmMeasures && !this.hasCehrtId;
  }

  get measuresMeetingCaseMinimum(): number {
    return this.selectedMeasures.filter((m) => (m.denominator ?? 0) >= 20)
      .length;
  }

  get meetsCaseMinimum(): boolean {
    if (this.selectedMeasures.length === 0) return false;
    return this.selectedMeasures.every((m) => (m.denominator ?? 0) >= 20);
  }

  get maxQualityPoints(): number {
    if (this.showTabs) {
      return this.currentTab === "mvp" ? 40 : 60;
    }
    return this.reportingType === "mvp" ? 40 : 60;
  }

  get estimatedPoints(): number {
    // Computed live from the currently selected measures' already-known points, rather than
    // read from the packet — the packet lags up to 2s behind selection changes (setupAutoSave's
    // debounce), which showed 0 right after selecting measures on a fresh, unsaved packet.
    const isMvpMode = this.showTabs ? this.currentTab === "mvp" : this.reportingType === "mvp";
    const measures = isMvpMode ? this.mvpMeasures : this.traditionalMeasures;
    const selectedIds = isMvpMode ? this.mvpSelectedIds : this.traditionalSelectedIds;
    const topCount = isMvpMode ? 4 : 6;

    return measures
      .filter((m) => selectedIds.has(m.measureId))
      .map((m) => m.calculatedPoints ?? 0)
      .sort((a, b) => b - a)
      .slice(0, topCount)
      .reduce((sum, points) => sum + points, 0);
  }

  get estimatedBonus(): number {
    return this.isSmallPractice ? 6 : 0;
  }

  get estimatedTotal(): number {
    return this.estimatedPoints + this.estimatedBonus;
  }

  get estimatedWeight(): number {
    const total = this.estimatedTotal;
    const maxPoints = this.maxQualityPoints;
    if (maxPoints === 0) return 0;
    const reweightedMaxWeight = this.reweighting().weights.quality;
    return Math.min((total / maxPoints) * reweightedMaxWeight, reweightedMaxWeight);
  }

  get reweightingTooltip(): string {
    return this.reweighting().explanationTooltip;
  }

  get showReweightingIndicator(): boolean {
    return this.reweighting().isReweighted;
  }

  get topMeasuresTooltip(): string {
    const topMeasures = this.getTopMeasures();
    const count = this.maxQualityPoints === 40 ? 4 : 6;
    if (topMeasures.length === 0) return `No measures selected`;

    let tooltip =
      `Top ${count} Measures:\n` +
      topMeasures
        .map(
          (m) => `${m.measureId}`,
        )
        .join("\n");

    if (this.isSmallPractice) {
      tooltip += `\n\nSmall Practice Bonus: +${this.estimatedBonus}`;
    }

    return tooltip;
  }

  ngOnInit(): void {
    this.qualityForm = this.fb.group({});

    setTimeout(() => {
      this.parentForm = this.ctrlContainer.form;
      if (this.parentForm) {
        this.parentForm.addControl("quality", this.qualityForm);
      }
    });

    this.store
      .select(selectActivePacket)
      .pipe(takeUntil(this.destroy$))
      .subscribe((packet) => {
        let packetChanged = false;
        if (packet?.id && packet.id !== this.currentPacketId) {
          packetChanged = true;
          this.currentPacketId = packet.id;
          this.isSmallPractice = this.questionnaire()?.specialStatusSmall === "yes";

          this.traditionalSelectedIds.clear();
          this.mvpSelectedIds.clear();

          this.loadMeasures();

        }

      });

    this.store
      .select(selectEffectiveQuestionnaire)
      .pipe(takeUntil(this.destroy$))
      .subscribe((questionnaire) => {
        const newIsSmallPractice = questionnaire?.specialStatusSmall === "yes";
        if (newIsSmallPractice !== this.isSmallPractice) {
          this.isSmallPractice = newIsSmallPractice;
          this.continueLoadMeasures();
          this.cdr.markForCheck();
        }
      });

    this.store
      .select(selectQualityMeasures)
      .pipe(takeUntil(this.destroy$))
      .subscribe((measures) => {
        if (this.currentPacketId && measures != null) {
          this.continueLoadMeasures();
          this.cdr.markForCheck();
        }
      });

    this.updateParentFormValidity();
    this.setupAutoSave();
  }

  ngOnDestroy(): void {
    if (this.parentForm) {
      this.parentForm.removeControl("quality");
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadMeasures(): void {
    this.continueLoadMeasures();
  }

  private continueLoadMeasures(): void {
    const measures = this.qualityMeasures();
    const packet = this.currentPacket();
    const mode = this.multiPacketMode();

    if (!measures || measures.length === 0) {
      this.allMeasures = [];
      this.traditionalMeasures = [];
      this.mvpMeasures = [];
      this.traditionalSelectedIds.clear();
      this.mvpSelectedIds.clear();
      this.updateParentFormValidity();
      return;
    }

    if (measures.length > 0) {
      const clonedMeasures = JSON.parse(JSON.stringify(measures)).filter(
        (m: any) => m.hasResults !== false,
      );

      const measureGroups = new Map<string, any[]>();

      clonedMeasures.forEach((m: any) => {
        const fullMeasureId =
          m.name || `measure-${Math.random().toString(36).substr(2, 9)}`;
        const baseMeasureId = this.mipsCalc.getBaseMeasureId(fullMeasureId);

        if (!measureGroups.has(baseMeasureId)) {
          measureGroups.set(baseMeasureId, []);
        }
        measureGroups.get(baseMeasureId)!.push(m);
      });

      this.measureStrataMap = measureGroups;

      const allMappedMeasures: any[] = [];

      measureGroups.forEach((group, baseMeasureId) => {
        const representativeRow = group.find((m: any) => m.name === baseMeasureId) ?? group[0];
        if (representativeRow) {
          allMappedMeasures.push(this.mapDashboardMeasure(representativeRow));
        }
      });

      this.allMeasures = allMappedMeasures;

      if (mode?.enabled) {
        this.traditionalMeasures = this.allMeasures.filter((m) => m.isMipsMeasure);
        this.mvpMeasures = this.allMeasures.filter((m) => m.isMvpMeasure);
      } else {
        if (this.reportingType === "mvp") {
          this.mvpMeasures = this.allMeasures.filter((m) => m.isMvpMeasure);
          this.traditionalMeasures = [];
        } else {
          this.traditionalMeasures = this.allMeasures.filter((m) => m.isMipsMeasure);
          this.mvpMeasures = [];
        }
      }

      if (packet?.quality?.selectedMeasureIds) {
        if (mode?.enabled) {
          const traditionalPacket = this.traditionalPacket();
          const mvpPacket = this.mvpPacket();

          if (traditionalPacket?.quality?.selectedMeasureIds) {
            traditionalPacket.quality.selectedMeasureIds.forEach((id: string) => {
              const exists = this.traditionalMeasures.some((m) => m.measureId === id);
              if (exists) {
                this.traditionalSelectedIds.add(id);
              }
            });
          }

          if (mvpPacket?.quality?.selectedMeasureIds) {
            mvpPacket.quality.selectedMeasureIds.forEach((id: string) => {
              const exists = this.mvpMeasures.some((m) => m.measureId === id);
              if (exists) {
                this.mvpSelectedIds.add(id);
              }
            });
          }
        } else {

          const selectedIds = packet.quality.selectedMeasureIds;

          if (this.reportingType === "traditional") {
            this.mvpSelectedIds.clear();
            selectedIds.forEach((id: string) => {
              if (this.traditionalMeasures.some((m) => m.measureId === id)) {
                this.traditionalSelectedIds.add(id);
              }
            });
          } else if (this.reportingType === "mvp") {
            this.traditionalSelectedIds.clear();
            selectedIds.forEach((id: string) => {
              if (this.mvpMeasures.some((m) => m.measureId === id)) {
                this.mvpSelectedIds.add(id);
              }
            });
          }
        }
        this.measureSelectionChanged$.next();
      } else {
        this.autoSelectTopMeasures();
        this.measureSelectionChanged$.next();
      }
    }

    this.updateParentFormValidity();
  }

  private autoSelectTopMeasures(): void {
    this.traditionalSelectedIds.clear();
    this.mvpSelectedIds.clear();

    // Sort by points but don't exclude 0-point measures — CMS requires reporting the full
    // measure count (6 traditional / 4 MVP) even when fewer than that many score above 0.
    const reportType = this.reportingType;
    if (reportType === "both" || reportType === "traditional") {
      [...this.traditionalMeasures]
        .sort((a, b) => (b.calculatedPoints ?? 0) - (a.calculatedPoints ?? 0))
        .slice(0, 6)
        .forEach((m) => this.traditionalSelectedIds.add(m.measureId));
    }
    if (reportType === "both" || reportType === "mvp") {
      [...this.mvpMeasures]
        .sort((a, b) => (b.calculatedPoints ?? 0) - (a.calculatedPoints ?? 0))
        .slice(0, 4)
        .forEach((m) => this.mvpSelectedIds.add(m.measureId));
    }
  }

  private setupAutoSave(): void {
    this.measureSelectionChanged$
      .pipe(debounceTime(2000), takeUntil(this.destroy$))
      .subscribe(() => {
        this.saveQualitySelections();
      });
  }

  private saveQualitySelections(): void {
    const packet = this.currentPacket();
    if (!packet?.id) {
      return;
    }

    let allSelectedMeasureIds: Set<string>;

    if (this.reportingType === "both") {
      allSelectedMeasureIds = new Set([
        ...this.traditionalSelectedIds,
        ...this.mvpSelectedIds,
      ]);
    } else if (this.reportingType === "mvp") {
      allSelectedMeasureIds = new Set(this.mvpSelectedIds);
    } else {
      allSelectedMeasureIds = new Set(this.traditionalSelectedIds);
    }

    if (allSelectedMeasureIds.size === 0) {
      return;
    }

    const measurements: QualityMeasurementDto[] = [];

    allSelectedMeasureIds.forEach((baseMeasureId) => {
      const strataGroup = this.measureStrataMap.get(baseMeasureId);

      if (strataGroup && strataGroup.length > 0) {
        strataGroup.forEach((stratum) => {
          if (stratum.isMultiPerformanceParent) return;

          measurements.push({
            measureId: baseMeasureId,
            numerator: stratum.numerator ?? 0,
            numeratorExclusion: null,
            denominator: stratum.denominator ?? 0,
            ipp: stratum.ipp ?? 0,
            denominatorException: stratum.denominatorException ?? null,
            denominatorExclusion: stratum.denominatorExclusion ?? null,
            isEndToEndReported: false,
            stratum: stratum.populationNbr?.toString() ?? null,
          });
        });
      }
    });

    const allSelectedMeasures = this.allMeasures.filter((m) =>
      allSelectedMeasureIds.has(m.measureId),
    );

    const traditionalSelected = this.traditionalMeasures.filter((m) =>
      this.traditionalSelectedIds.has(m.measureId),
    );
    const mvpSelected = this.mvpMeasures.filter((m) =>
      this.mvpSelectedIds.has(m.measureId),
    );

    const quality: QualityCategory = {
      traditionalMeasures: this.traditionalMeasures,
      traditionalMeasuresSelected: traditionalSelected.length,
      mvpMeasures: this.mvpMeasures,
      mvpMeasuresSelected: mvpSelected.length,
      allMeasures: this.allMeasures,
      selectedMeasureIds: Array.from(allSelectedMeasureIds),
      hasOutcomeHighPriority: this.hasOutcomeHighPriority,
      meetsCaseMinimum: this.meetsCaseMinimum,
      estimatedPoints: this.estimatedPoints,
      estimatedBonus: this.estimatedBonus,
      estimatedTotal: this.estimatedTotal,
      estimatedWeight: this.estimatedWeight,
      maxWeight: this.reweighting().weights.quality,
      minimumMeasuresRequired: this.minimumRequired,
    };

    this.store.dispatch(
      MipsPacketActions.saveQualityMeasurements({
        measurements,
        quality,
        reportingType: this.reportingType,
        traditionalMeasureIds: Array.from(this.traditionalSelectedIds),
        mvpMeasureIds: Array.from(this.mvpSelectedIds),
      }),
    );
  }


  private mapDashboardMeasure(m: any): QualityMeasure {
    const performanceValue = m.performance ?? m.value ?? 0;
    const performancePercent =
      performanceValue <= 1 ? performanceValue * 100 : performanceValue;

    const baseMeasureId =
      m.name || `measure-${Math.random().toString(36).substr(2, 9)}`;

    const decileRows = this.measureDeciles.filter(
      (d) => d.measureID === baseMeasureId,
    );

    const decilePoints =
      decileRows.length > 0
        ? this.extractDecilePointsFromRows(decileRows)
        : undefined;
    const decileRanges =
      decileRows.length > 0
        ? this.extractDecileRangesFromRows(decileRows)
        : undefined;

    const calculatedPoints = m.calculatedPoints ?? 0;

    const result: QualityMeasure = {
      measureId: baseMeasureId,
      fullMeasureId: baseMeasureId,
      measureTitle: m.measureDescription || m.measureDisplayName || "",
      isOutcome:
        m.outcome === true || m.outcome === "true" || m.isOutcome === true,
      isHighPriority:
        m.highpriority === true ||
        m.highpriority === "true" ||
        m.isHighPriority === true,
      isMvpMeasure: m.isMVP === true,
      isMipsMeasure: m.isMIPS === true,
      numerator: m.numerator ?? 0,
      denominator: m.denominator ?? 0,
      denominatorExclusion: m.denominatorExclusion ?? 0,
      denominatorException: m.denominatorException ?? 0,

      performance: Math.round(performancePercent * 10) / 10,
      points: m.points || 0,
      selected: false,
      achievedPerformance: performancePercent,
      cmsBenchmark: m.nationalBenchMark ? m.nationalBenchMark * 100 : undefined,
      registryAvg: m.registryBenchMark ? m.registryBenchMark * 100 : undefined,
      calculatedPoints: calculatedPoints,
      inverse: m.inverse,
      floor: m.floor,
      sevenPointCap: m.sevenPointCap,
      noBenchmark: m.noBenchmark,
      decilePoints,
      decileRanges,
      isMultiPerformance: m.isMultiPerformance,
      isMultiPerformanceParent: m.isMultiPerformanceParent,
      measureType: m.measureType,
    };

    return result;
  }

  private extractDecilePointsFromRows(decileRows: any[]): number[] {
    const sorted = [...decileRows].sort((a, b) => a.decile - b.decile);

    const points: number[] = [];
    for (let decileNumber = 1; decileNumber <= 10; decileNumber++) {
      const row = sorted.find((r) => r.decile === decileNumber);
      const pointValue = row?.decile ?? -1;
      points.push(pointValue);
    }
    return points;
  }

  private extractDecileRangesFromRows(decileRows: any[]): string[] {
    const sorted = [...decileRows].sort((a, b) => a.decile - b.decile);

    const ranges: string[] = [];
    for (let decileNumber = 1; decileNumber <= 10; decileNumber++) {
      const row = sorted.find((r) => r.decile === decileNumber);
      if (row && row.lowerLimit !== undefined && row.upperLimit !== undefined) {
        ranges.push(`${row.lowerLimit} - ${row.upperLimit}`);
      } else {
        ranges.push("N/A");
      }
    }
    return ranges;
  }

  private getTopMeasures(): QualityMeasure[] {
    const selected = this.selectedMeasures.filter(
      (m) => m.calculatedPoints !== undefined && m.calculatedPoints > 0,
    );
    const sorted = selected.sort(
      (a, b) => (b.calculatedPoints || 0) - (a.calculatedPoints || 0),
    );

    const filtered = this.mipsCalc.filterEquivalentMeasures(sorted, m => m.measureId);

    const topCount = this.maxQualityPoints === 40 ? 4 : 6;
    return filtered.slice(0, topCount);
  }


  toggleExpansion(measureId: string): void {
    this.expandedMeasureId =
      this.expandedMeasureId === measureId ? null : measureId;
  }

  isMeasureDisabled(measure: QualityMeasure): boolean {
    const targetSet =
      (this.reportingType === "both" && this.currentTab === "mvp") ||
      (!this.showTabs && this.reportingType === "mvp")
        ? this.mvpSelectedIds
        : this.traditionalSelectedIds;

    if (targetSet.has(measure.measureId)) {
      return false;
    }
    const baseMeasureId = this.mipsCalc.getBaseMeasureId(measure.measureId);
    const equivalentPairs = measureDataJson.equivalentMeasures;

    const equivalent = equivalentPairs.find(
      (eq: any) =>
        eq.measureId === baseMeasureId || eq.cmsEcqmId === baseMeasureId,
    );

    if (!equivalent) {
      return false;
    }

    const otherMeasure =
      equivalent.measureId === baseMeasureId
        ? equivalent.cmsEcqmId
        : equivalent.measureId;

    for (const selectedId of targetSet) {
      const selectedBase = this.mipsCalc.getBaseMeasureId(selectedId);
      if (selectedBase === otherMeasure) {
        return true;
      }
    }

    return false;
  }

  getDisabledTooltip(measure: QualityMeasure): string {
    const baseMeasureId = this.mipsCalc.getBaseMeasureId(measure.measureId);
    const equivalentPairs = measureDataJson.equivalentMeasures;

    const equivalent = equivalentPairs.find(
      (eq: any) =>
        eq.measureId === baseMeasureId || eq.cmsEcqmId === baseMeasureId,
    );

    if (!equivalent) {
      return "";
    }

    const otherMeasure =
      equivalent.measureId === baseMeasureId
        ? equivalent.cmsEcqmId
        : equivalent.measureId;

    return `Cannot select because equivalent measure ${otherMeasure} is already selected`;
  }

  isMeasureSelected(measure: QualityMeasure): boolean {
    if (this.reportingType === "both") {
      return this.currentTab === "traditional"
        ? this.traditionalSelectedIds.has(measure.measureId)
        : this.mvpSelectedIds.has(measure.measureId);
    } else if (this.reportingType === "mvp") {
      return this.mvpSelectedIds.has(measure.measureId);
    } else {
      return this.traditionalSelectedIds.has(measure.measureId);
    }
  }

  onMeasureToggle(): void {
    this.updateParentFormValidity();
    this.measureSelectionChanged$.next();

    this.cdr.markForCheck();
  }

  onCheckboxChange(measure: QualityMeasure, checked: boolean): void {
    if (this.isMeasureDisabled(measure)) {
      return;
    }

    if (this.reportingType === "both") {
      if (this.currentTab === "traditional") {
        if (checked) {
          this.traditionalSelectedIds.add(measure.measureId);
        } else {
          this.traditionalSelectedIds.delete(measure.measureId);
        }
      } else {
        if (checked) {
          this.mvpSelectedIds.add(measure.measureId);
        } else {
          this.mvpSelectedIds.delete(measure.measureId);
        }
      }
    } else if (this.reportingType === "mvp") {
      if (checked) {
        this.mvpSelectedIds.add(measure.measureId);
      } else {
        this.mvpSelectedIds.delete(measure.measureId);
      }
    } else {
      if (checked) {
        this.traditionalSelectedIds.add(measure.measureId);
      } else {
        this.traditionalSelectedIds.delete(measure.measureId);
      }
    }

    this.onMeasureToggle();
  }

  onTabChange(tab: "traditional" | "mvp"): void {
    this.currentTab = tab;
  }

  private updateParentFormValidity(): void {
    if (!this.parentForm) return;

    const excluded = this.validationService.getExcludedCategories(this.questionnaire());
    if (excluded.has("quality")) {
      this.parentForm.setErrors(null);
      return;
    }

    const isValid = this.validateSelection();
    if (isValid) {
      this.parentForm.setErrors(null);
    } else {
      this.parentForm.setErrors({ incomplete: true });
    }
  }

  validateSelection(): boolean {
    const type = this.showTabs ? this.currentTab : this.reportingType;

    const minRequired = 3;
    if (this.selectedMeasures.length < minRequired) return false;

    if (!this.hasOutcomeHighPriority) return false;

    if (this.reportingType === "both") {
      const traditionalValid = this.traditionalSelectedCount >= 3;
      const mvpValid = this.mvpSelectedCount >= 3;

      const traditionalHasOutcome = this.traditionalMeasures
        .filter((m) => this.traditionalSelectedIds.has(m.measureId))
        .some((m) => m.isOutcome || m.isHighPriority);

      const mvpHasOutcome = this.mvpMeasures
        .filter((m) => this.mvpSelectedIds.has(m.measureId))
        .some((m) => m.isOutcome || m.isHighPriority);

      return (
        traditionalValid && mvpValid && traditionalHasOutcome && mvpHasOutcome
      );
    }

    return true;
  }

  private formatDate(date: string | Date): string {
    if (!date) return "";
    const d = new Date(date);
    return `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d
      .getDate()
      .toString()
      .padStart(2, "0")}/${d.getFullYear()}`;
  }

  onSmallPracticeChange(): void {
    this.cdr.detectChanges();

  }

  getNumerator(measure: QualityMeasure): number {
    return measure.numerator ?? 0;
  }

  getDenominator(measure: QualityMeasure): number {
    return measure.denominator ?? 0;
  }

  getDecileDisplay(measure: QualityMeasure): string {
    if (!measure.decileRanges || !measure.decilePoints) {
      return "<p>No decile data available</p>";
    }

    let display =
      '<table class="decile-table"><thead><tr><th>Decile Points</th>';

    for (let i = 0; i < measure.decileRanges.length; i++) {
      if (measure.decilePoints[i] >= 0) {
        display += `<th>${i + 1}</th>`;
      }
    }

    display +=
      "</tr></thead><tbody><tr><td><strong>Performance Rate Range</strong></td>";

    for (let i = 0; i < measure.decileRanges.length; i++) {
      if (measure.decilePoints[i] >= 0) {
        display += `<td>${measure.decileRanges[i]}</td>`;
      }
    }

    display += "</tr></tbody></table>";
    return display;
  }
}
